import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SPECIALTY_COLORS = {
  'GP (General Practitioner)': 'bg-blue-100 text-blue-700',
  'Neurologist': 'bg-purple-100 text-purple-700',
  'Cardiologist': 'bg-red-100 text-red-700',
  'Dermatologist': 'bg-yellow-100 text-yellow-700',
  'Orthopaedic Surgeon': 'bg-orange-100 text-orange-700',
  'Gastroenterologist': 'bg-green-100 text-green-700',
  'Endocrinologist': 'bg-teal-100 text-teal-700',
  'Rheumatologist': 'bg-indigo-100 text-indigo-700',
  'Pulmonologist': 'bg-cyan-100 text-cyan-700',
  'Psychiatrist': 'bg-pink-100 text-pink-700',
};

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const getCountdownInfo = (appointmentDate) => {
  const now = new Date();
  const appt = new Date(appointmentDate);
  const diffMs = appt?.getTime() - now?.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 0) return { label: 'Past', color: 'text-muted-foreground', urgent: false };
  if (diffHours < 2) return { label: 'In < 2h', color: 'text-error font-semibold', urgent: true };
  if (diffHours < 24) return { label: `In ${Math.round(diffHours)}h`, color: 'text-warning font-semibold', urgent: true };
  if (diffDays === 1) return { label: 'Tomorrow', color: 'text-warning font-semibold', urgent: true };
  return { label: `In ${diffDays}d`, color: 'text-success', urgent: false };
};

const toDateKey = (date) => {
  const y = date?.getFullYear();
  const m = String(date?.getMonth() + 1)?.padStart(2, '0');
  let d = String(date?.getDate())?.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const AppointmentCalendarView = ({ appointments, onEdit, onDelete, onAddForDate }) => {
  const navigate = useNavigate();
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDateKey, setSelectedDateKey] = useState(null);

  // Build a map: dateKey -> [appointments]
  const appointmentsByDate = useMemo(() => {
    const map = {};
    appointments?.forEach(appt => {
      const key = toDateKey(new Date(appt.appointment_date));
      if (!map?.[key]) map[key] = [];
      map?.[key]?.push(appt);
    });
    return map;
  }, [appointments]);

  // Build calendar grid days
  const calendarDays = useMemo(() => {
    const year = currentMonth?.getFullYear();
    const month = currentMonth?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Monday=0 offset
    let startDow = firstDay?.getDay(); // 0=Sun
    startDow = startDow === 0 ? 6 : startDow - 1; // convert to Mon=0

    const days = [];
    // Pad start
    for (let i = 0; i < startDow; i++) {
      let d = new Date(year, month, 1 - (startDow - i));
      days?.push({ date: d, currentMonth: false });
    }
    // Current month days
    for (let d = 1; d <= lastDay?.getDate(); d++) {
      days?.push({ date: new Date(year, month, d), currentMonth: true });
    }
    // Pad end to complete last row
    const remaining = 7 - (days?.length % 7);
    if (remaining < 7) {
      for (let i = 1; i <= remaining; i++) {
        days?.push({ date: new Date(year, month + 1, i), currentMonth: false });
      }
    }
    return days;
  }, [currentMonth]);

  const monthLabel = currentMonth?.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  const handleDayClick = (dayObj) => {
    const key = toDateKey(dayObj?.date);
    const hasAppts = !!appointmentsByDate?.[key];
    if (hasAppts) {
      setSelectedDateKey(prev => prev === key ? null : key);
    } else {
      // No appointment — trigger add with pre-filled date
      const dateStr = dayObj?.date?.toISOString()?.slice(0, 10);
      onAddForDate?.(dateStr);
    }
  };

  const selectedAppointments = selectedDateKey ? (appointmentsByDate?.[selectedDateKey] || []) : [];

  const handlePrepareReport = (appointment) => {
    const apptDateObj = new Date(appointment.appointment_date);
    const startDate = new Date(apptDateObj);
    startDate?.setDate(startDate?.getDate() - 30);
    navigate('/prepare-for-doctor-visit', {
      state: {
        fromAppointment: true,
        appointmentId: appointment?.id,
        doctorName: appointment?.doctor_name,
        specialty: appointment?.specialty,
        appointmentDate: appointment?.appointment_date,
        dateRangeStart: startDate?.toISOString(),
        dateRangeEnd: appointment?.appointment_date
      }
    });
  };

  const todayKey = toDateKey(today);

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <button
            onClick={prevMonth}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Previous month"
          >
            <Icon name="ChevronLeft" size={18} className="text-muted-foreground" />
          </button>
          <h3 className="text-sm font-semibold text-foreground">{monthLabel}</h3>
          <button
            onClick={nextMonth}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Next month"
          >
            <Icon name="ChevronRight" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Day labels */}
        <div className="grid grid-cols-7 border-b border-border">
          {DAY_LABELS?.map(day => (
            <div key={day} className="py-2 text-center text-xs font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {calendarDays?.map((dayObj, idx) => {
            const key = toDateKey(dayObj?.date);
            const appts = appointmentsByDate?.[key] || [];
            const hasAppts = appts?.length > 0;
            const isToday = key === todayKey;
            const isSelected = key === selectedDateKey;
            const isPast = dayObj?.date < today && !isToday;
            const firstAppt = appts?.[0];
            const countdown = firstAppt ? getCountdownInfo(firstAppt?.appointment_date) : null;

            return (
              <button
                key={idx}
                onClick={() => handleDayClick(dayObj)}
                className={`
                  relative min-h-[56px] p-1 flex flex-col items-center border-b border-r border-border/40
                  transition-colors touch-manipulation
                  ${!dayObj?.currentMonth ? 'opacity-30' : ''}
                  ${isSelected && hasAppts ? 'bg-primary/10' : ''}
                  ${!isSelected && hasAppts ? 'hover:bg-primary/5' : ''}
                  ${!hasAppts && dayObj?.currentMonth ? 'hover:bg-muted/50' : ''}
                  ${isPast && !hasAppts ? 'cursor-default' : 'cursor-pointer'}
                `}
                aria-label={`${dayObj?.date?.toLocaleDateString('en-GB')}${hasAppts ? `, ${appts?.length} appointment(s)` : ''}`}
              >
                {/* Date number */}
                <span className={`
                  w-7 h-7 flex items-center justify-center rounded-full text-xs font-medium mt-0.5
                  ${isToday ? 'bg-primary text-primary-foreground font-bold' : ''}
                  ${isSelected && hasAppts && !isToday ? 'bg-primary/20 text-primary font-semibold' : ''}
                  ${!isToday && !isSelected ? 'text-foreground' : ''}
                `}>
                  {dayObj?.date?.getDate()}
                </span>
                {/* Appointment indicator */}
                {hasAppts && (
                  <>
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-0.5" />
                    {countdown && (
                      <span className={`text-[9px] leading-tight mt-0.5 text-center px-0.5 ${countdown?.color} truncate w-full`}>
                        {countdown?.label}
                      </span>
                    )}
                    {firstAppt?.doctor_name && (
                      <span className="text-[8px] text-muted-foreground truncate w-full text-center leading-tight">
                        {firstAppt?.doctor_name?.split(' ')?.slice(-1)?.[0]}
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {/* Empty state hint */}
      {appointments?.length === 0 && (
        <div className="text-center py-6 px-4">
          <p className="text-sm text-muted-foreground">
            <Icon name="CalendarPlus" size={16} className="inline mr-1 text-primary" />
            Tap any date to add an appointment
          </p>
        </div>
      )}
      {/* Inline appointment detail panel */}
      {selectedDateKey && selectedAppointments?.length > 0 && (
        <div className="bg-card border border-primary/30 rounded-xl p-4 shadow-sm space-y-3 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-semibold text-foreground">
              {new Date(selectedDateKey + 'T00:00:00')?.toLocaleDateString('en-GB', {
                weekday: 'long', day: 'numeric', month: 'long'
              })}
            </h4>
            <button
              onClick={() => setSelectedDateKey(null)}
              className="p-1 rounded-lg hover:bg-muted transition-colors"
              aria-label="Close detail panel"
            >
              <Icon name="X" size={16} className="text-muted-foreground" />
            </button>
          </div>

          {selectedAppointments?.map(appt => {
            const apptDate = new Date(appt.appointment_date);
            const countdown = getCountdownInfo(appt?.appointment_date);
            const specialtyColor = SPECIALTY_COLORS?.[appt?.specialty] || 'bg-muted text-muted-foreground';
            const formattedTime = apptDate?.toLocaleTimeString('en-GB', {
              hour: 'numeric', minute: '2-digit', hour12: true
            });

            return (
              <div key={appt?.id} className={`border rounded-xl p-3 ${countdown?.urgent ? 'border-warning/40 bg-warning/5' : 'border-border bg-background'}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="Stethoscope" size={16} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{appt?.doctor_name}</p>
                      {appt?.specialty && (
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium mt-0.5 ${specialtyColor}`}>
                          {appt?.specialty}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`text-xs flex-shrink-0 ${countdown?.color}`}>{countdown?.label}</span>
                </div>
                <div className="space-y-1 mb-3">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Clock" size={12} className="flex-shrink-0" />
                    <span>{formattedTime}</span>
                  </div>
                  {appt?.location && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="MapPin" size={12} className="flex-shrink-0" />
                      <span className="truncate">{appt?.location}</span>
                    </div>
                  )}
                  {appt?.notes && (
                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                      <Icon name="FileText" size={12} className="flex-shrink-0 mt-0.5" />
                      <span className="line-clamp-2">{appt?.notes}</span>
                    </div>
                  )}
                </div>
                {/* Reminder dots */}
                <div className="flex items-center gap-1.5 mb-3">
                  <div className={`w-2 h-2 rounded-full ${appt?.reminder_7day_sent ? 'bg-success' : 'bg-muted'}`} title="7-day reminder" />
                  <div className={`w-2 h-2 rounded-full ${appt?.reminder_1day_sent ? 'bg-success' : 'bg-muted'}`} title="1-day reminder" />
                  <div className={`w-2 h-2 rounded-full ${appt?.reminder_2hour_sent ? 'bg-success' : 'bg-muted'}`} title="2-hour reminder" />
                  <span className="text-xs text-muted-foreground ml-1">Reminders</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handlePrepareReport(appt)}
                    iconName="ClipboardList"
                    iconPosition="left"
                    className="flex-1 text-xs"
                  >
                    Prepare Report
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setSelectedDateKey(null); onEdit(appt); }}
                    iconName="Pencil"
                    className="px-2.5"
                    aria-label="Edit appointment"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(appt?.id)}
                    iconName="Trash2"
                    className="px-2.5 text-error border-error/30 hover:bg-error/10"
                    aria-label="Delete appointment"
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendarView;
